// FILE: src/pages/AdminDashboardPage.jsx
// The admin overview (plan WP-8 / WP-7; matrix 9.8, 10.2, 11.x; R3, B-14).
// Five groups, each loading on its own so a slow or failed request never
// blanks the page: students, learning time (core and flex, for a date
// range), quiz results (for a date range), support and survey, and monthly
// trends (3, 6, 12 or 24 months). Every figure is the server's, computed
// from the real records; untimed (reviewer) accounts are left out. The
// trends chart follows the theme, tells its lines apart by shape as well as
// colour, and has a table view with the same figures (WCAG 1.4.1).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import GradingOutlinedIcon from "@mui/icons-material/GradingOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import {
  getEnrollmentStats,
  getMonthlyTrends,
  getQuizStats,
  getSupportFeedbackStats,
  getTimeStats
} from "../features/dashboardSlice";
import { EmptyState, ErrorState, LoadingState, PageHeader, Section, StatCard } from "../components/ui";
import { FigureGroup, ResponsiveTable } from "../components/admin/AdminBits";
import { DASH, SLA_HOURS, formatHours, formatNumber } from "../components/admin/format";
import { RANGE_PRESETS, presetRange } from "../components/admin/dates";

const PERIODS = [
  { value: "3months", label: "Last 3 months" },
  { value: "6months", label: "Last 6 months" },
  { value: "1year", label: "Last 12 months" },
  { value: "2years", label: "Last 2 years" }
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** What the trends show: students (new, completions, invitations) or learning time (hours). */
const MEASURES = {
  students: {
    label: "Students",
    table: "New students and completions per month",
    chart:
      "Line chart of new students (solid line, round points) and course completions (dashed line, square points) per month. Choose Show as table for the same figures.",
    lines: [
      { key: "students", name: "New students" },
      { key: "completions", name: "Completions" }
    ],
    columns: [
      { key: "students", label: "New students" },
      { key: "completions", label: "Completions" },
      { key: "invites", label: "Invitations sent" }
    ],
    decimals: 0
  },
  time: {
    label: "Learning time",
    table: "Credited learning hours per month",
    chart:
      "Line chart of credited core hours (chapters, solid line, round points) and flex hours (homelinks, dashed line, square points) per month. Choose Show as table for the same figures.",
    lines: [
      { key: "coreHours", name: "Core hours (chapters)" },
      { key: "flexHours", name: "Flex hours (homelinks)" }
    ],
    columns: [
      { key: "coreHours", label: "Core hours" },
      { key: "flexHours", label: "Flex hours" },
      { key: "totalHours", label: "All hours" }
    ],
    decimals: 1
  }
};

/**
 * The monthly series merged by month and sorted (missing months = 0):
 * students, completions and invites (counts) and core / flex / total hours.
 */
export function mergeMonthlyData(studentsArr = [], completionsArr = [], invitesArr = [], timeArr = []) {
  const map = {};
  const row = (month, year) => {
    const key = `${year}-${month}`;
    map[key] = map[key] || { month, year, students: 0, completions: 0, invites: 0, coreHours: 0, flexHours: 0, totalHours: 0 };
    return map[key];
  };
  const add = (arr, field) =>
    (Array.isArray(arr) ? arr : []).forEach(({ month, year, count }) => {
      if (!month || !year) return;
      row(month, year)[field] = Number(count) || 0;
    });
  add(studentsArr, "students");
  add(completionsArr, "completions");
  add(invitesArr, "invites");
  (Array.isArray(timeArr) ? timeArr : []).forEach((t) => {
    if (!t?.month || !t?.year) return;
    const r = row(t.month, t.year);
    r.coreHours = Number(t.inClassHours) || 0;
    r.flexHours = Number(t.homeLinkHours) || 0;
    r.totalHours = Number(t.totalHours) || 0;
  });
  return Object.values(map)
    .sort((a, b) => (a.year === b.year ? a.month - b.month : a.year - b.year))
    .map((r) => ({ ...r, monthLabel: `${MONTHS[r.month - 1] || r.month} ${r.year}` }));
}

/** One group's three states: loading, failed (with retry) or its cards. */
function GroupBody({ status, error, onRetry, hasData, label, children, cards = 3 }) {
  if (!hasData && status === "failed") {
    return (
      <Box sx={{ gridColumn: "1 / -1" }}>
        <ErrorState error={error} title={`We couldn't load ${label}`} onRetry={onRetry} />
      </Box>
    );
  }
  if (!hasData) {
    return (
      <Box sx={{ gridColumn: "1 / -1" }}>
        <LoadingState variant="cards" rows={cards} label={`Loading ${label}…`} />
      </Box>
    );
  }
  return (
    <>
      {status === "loading" && (
        <Box sx={{ gridColumn: "1 / -1" }}>
          <LinearProgress aria-label={`Updating ${label}`} />
        </Box>
      )}
      {status === "failed" && (
        <Box sx={{ gridColumn: "1 / -1" }}>
          <ErrorState compact error={error} onRetry={onRetry} />
        </Box>
      )}
      {children}
    </>
  );
}

function RangeSelect({ id, label, value, onChange }) {
  return (
    <TextField select id={id} label={label} value={value} onChange={(e) => onChange(e.target.value)} sx={{ minWidth: 220 }}>
      {RANGE_PRESETS.map((p) => (
        <MenuItem key={p.value} value={p.value}>
          {p.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

function TrendsChart({ data, measure }) {
  const theme = useTheme();
  // CSS variables, so the lines switch with the light/dark theme.
  const c = (theme.vars || theme).palette;
  const tick = { fill: c.text.secondary, fontSize: "1rem" };
  const m = MEASURES[measure];
  const [first, second] = m.lines;
  return (
    <Box role="img" aria-label={m.chart} sx={{ width: "100%", height: 320 }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke={c.divider} strokeDasharray="3 3" />
          <XAxis dataKey="monthLabel" tick={tick} stroke={c.zun?.inputBorder || c.divider} tickMargin={8} />
          <YAxis allowDecimals={m.decimals > 0} tick={tick} stroke={c.zun?.inputBorder || c.divider} width={48} />
          <Tooltip
            contentStyle={{
              background: c.background.paper,
              border: `1px solid ${c.divider}`,
              borderRadius: 10,
              color: c.text.primary,
              fontSize: "1rem"
            }}
            labelStyle={{ color: c.text.primary, fontWeight: 600 }}
          />
          <Legend wrapperStyle={{ color: c.text.primary, fontSize: "1rem", paddingTop: 8 }} />
          <Line
            type="monotone"
            dataKey={first.key}
            name={first.name}
            legendType="plainline"
            stroke={c.primary.main}
            strokeWidth={3}
            dot={{ r: 4, fill: c.primary.main }}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey={second.key}
            name={second.name}
            legendType="plainline"
            stroke={c.info.main}
            strokeWidth={3}
            strokeDasharray="8 5"
            dot={(props) => {
              const { cx, cy, index } = props;
              if (cx == null || cy == null) return null;
              return <rect key={`sq-${index}`} x={cx - 4} y={cy - 4} width={8} height={8} fill={c.info.main} />;
            }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

function TrendsTable({ data, periodLabel, measure }) {
  const m = MEASURES[measure];
  return (
    <ResponsiveTable label={`${m.table}, ${periodLabel.toLowerCase()}`} minWidth={480}>
      <TableHead>
        <TableRow>
          <TableCell component="th" scope="col">
            Month
          </TableCell>
          {m.columns.map((col) => (
            <TableCell key={col.key} component="th" scope="col" align="right">
              {col.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {data.map((row) => (
          <TableRow key={`${row.year}-${row.month}`}>
            <TableCell component="th" scope="row">
              {row.monthLabel}
            </TableCell>
            {m.columns.map((col) => (
              <TableCell key={col.key} align="right">
                {formatNumber(row[col.key], m.decimals)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </ResponsiveTable>
  );
}

export default function AdminDashboardPage() {
  const dispatch = useDispatch();
  const { monthlyTrends, supportFeedbackStats, enrollmentStats, timeStats, quizStats, status = {}, errors = {} } = useSelector(
    (state) => state.dashboard
  );
  const [period, setPeriod] = useState("1year");
  const [measure, setMeasure] = useState("students");
  const [view, setView] = useState("chart");
  const [timeRange, setTimeRange] = useState("all");
  const [quizRange, setQuizRange] = useState("all");

  const loadEnrollment = useCallback(() => dispatch(getEnrollmentStats()), [dispatch]);
  const loadTime = useCallback(() => dispatch(getTimeStats(presetRange(timeRange))), [dispatch, timeRange]);
  const loadQuiz = useCallback(() => dispatch(getQuizStats(presetRange(quizRange))), [dispatch, quizRange]);
  const loadSupport = useCallback(() => dispatch(getSupportFeedbackStats()), [dispatch]);
  const loadTrends = useCallback(() => dispatch(getMonthlyTrends(period)), [dispatch, period]);

  useEffect(() => {
    loadEnrollment();
    loadSupport();
  }, [loadEnrollment, loadSupport]);
  useEffect(() => {
    loadTime();
  }, [loadTime]);
  useEffect(() => {
    loadQuiz();
  }, [loadQuiz]);
  // B-14: the period selector refetches.
  useEffect(() => {
    loadTrends();
  }, [loadTrends]);

  const chartData = useMemo(
    () =>
      mergeMonthlyData(
        monthlyTrends?.studentsPerMonth,
        monthlyTrends?.completionsPerMonth,
        monthlyTrends?.invitesPerMonth,
        monthlyTrends?.timeUsagePerMonth
      ),
    [monthlyTrends]
  );
  const hasMeasure = measure === "time" ? Array.isArray(monthlyTrends?.timeUsagePerMonth) : true;
  const periodLabel = PERIODS.find((p) => p.value === period)?.label || "";

  const e = enrollmentStats || {};
  const t = timeStats || {};
  const q = quizStats || {};
  const tickets = supportFeedbackStats?.tickets || {};
  const feedback = supportFeedbackStats?.feedback || {};
  const avgResolution = Number(tickets.avgResolutionHours);
  const slow = Number.isFinite(avgResolution) && avgResolution > SLA_HOURS;
  const rating = Number(feedback.averageRating);
  const quizMarked = (Number(q.passed) || 0) + (Number(q.failed) || 0);
  const quizAvg = Number(q.averageScore);
  const untimed = Number(e.untimed) || 0;

  return (
    <Box>
      <PageHeader
        title="Admin Dashboard"
        description="How the course is running: students, learning time, quiz results, support requests and the course survey. Every figure comes from the students' records."
        actions={
          <Button component={RouterLink} to="/admin/users/add" variant="contained" startIcon={<PersonAddAlt1OutlinedIcon />}>
            Add student
          </Button>
        }
      />

      <FigureGroup
        id="students"
        title="Students"
        description={
          enrollmentStats
            ? `Everyone with a student account. ${formatNumber(e.registered)} registered, ${formatNumber(e.chapters)} chapters in the course.${
                untimed ? ` ${formatNumber(untimed)} untimed (reviewer) ${untimed === 1 ? "account isn't" : "accounts aren't"} counted.` : ""
              }`
            : "Everyone with a student account."
        }
        columns={4}
        action={
          <Button component={RouterLink} to="/admin/users" variant="outlined">
            View students
          </Button>
        }
      >
        <GroupBody
          status={status.enrollment}
          error={errors.enrollment}
          onRetry={loadEnrollment}
          hasData={!!enrollmentStats}
          label="student figures"
          cards={8}
        >
          <StatCard icon={PeopleOutlineIcon} label="All students" value={formatNumber(e.totalStudents)} testId="kpi-students" />
          <StatCard
            icon={MailOutlinedIcon}
            label="Invitation sent"
            value={formatNumber(e.invited)}
            note="Haven't finished registering"
            testId="kpi-invited"
          />
          <StatCard
            icon={PendingOutlinedIcon}
            label="Not started"
            value={formatNumber(e.notStarted)}
            note="Registered, no lesson time yet"
            testId="kpi-not-started"
          />
          <StatCard
            icon={HourglassEmptyOutlinedIcon}
            label="In progress"
            value={formatNumber(e.inProgress)}
            note="Started, not every chapter done"
            testId="kpi-in-progress"
          />
          <StatCard
            icon={TaskAltOutlinedIcon}
            label="Completed"
            value={formatNumber(e.completed)}
            note={e.surveyPending ? `${formatNumber(e.surveyPending)} still to send the survey` : "Finished every chapter"}
            testId="kpi-completed"
          />
          <StatCard
            icon={LockOutlinedIcon}
            label="Locked accounts"
            value={formatNumber(e.locked)}
            tone={Number(e.locked) > 0 ? "error" : "primary"}
            note="Only support can unlock an account"
            testId="kpi-locked"
          />
          <StatCard
            icon={EventOutlinedIcon}
            label="Enrolment ending soon"
            value={formatNumber(e.expiring)}
            tone={Number(e.expiring) > 0 ? "warning" : "primary"}
            note="Within the next 30 days"
            testId="kpi-expiring"
          />
          <StatCard
            icon={EventBusyOutlinedIcon}
            label="Enrolment ended"
            value={formatNumber(e.enrolmentEnded)}
            note="The one-year enrolment is over"
            testId="kpi-ended"
          />
        </GroupBody>
      </FigureGroup>

      <FigureGroup
        id="time"
        title="Learning time"
        description={
          timeStats
            ? `Time credited by the course's time rules: ${formatHours(t.totalHours)} in all, to ${formatNumber(t.studentsWithTime)} ${
                Number(t.studentsWithTime) === 1 ? "student" : "students"
              }.`
            : "Time credited to students by the course's time rules."
        }
        columns={4}
        action={<RangeSelect id="time-range" label="Learning time for" value={timeRange} onChange={setTimeRange} />}
      >
        <GroupBody status={status.time} error={errors.time} onRetry={loadTime} hasData={!!timeStats} label="learning time" cards={4}>
          <StatCard
            icon={MenuBookOutlinedIcon}
            label="Core time (chapters)"
            value={formatHours(t.totalInClassHours)}
            note="All students, in-class lessons"
            testId="kpi-core"
          />
          <StatCard
            icon={HomeWorkOutlinedIcon}
            label="Flex time (homelinks)"
            value={formatHours(t.totalHomeLinkHours)}
            note={
              Number(t.totalUnattributedHours) > 0
                ? `Plus ${formatHours(t.totalUnattributedHours)} not linked to a lesson`
                : "All students, flexible hours"
            }
            testId="kpi-flex"
          />
          <StatCard
            icon={TimerOutlinedIcon}
            label="Average day"
            value={formatHours(t.avgDailyUsage)}
            note={`Per student, over ${formatNumber(t.activeStudentDays)} days with time`}
            testId="kpi-average-day"
          />
          <StatCard
            icon={FlagOutlinedIcon}
            label="Reached the 5-hour limit"
            value={formatNumber(t.exceededDailyLimitCount)}
            note={`${Number(t.exceededDailyLimitCount) === 1 ? "Student" : "Students"} at the daily cap at least once (${formatNumber(t.cappedDays)} ${
              Number(t.cappedDays) === 1 ? "day" : "days"
            })`}
            testId="kpi-capped"
          />
        </GroupBody>
      </FigureGroup>

      <FigureGroup
        id="quiz"
        title="Quiz results"
        description="Scores recorded when students finish a lesson's quiz, every attempt counted."
        columns={4}
        action={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" } }}>
            <RangeSelect id="quiz-range" label="Quiz results for" value={quizRange} onChange={setQuizRange} />
            <Button component={RouterLink} to="/admin/quiz-results" variant="outlined">
              Results by lesson
            </Button>
          </Stack>
        }
      >
        <GroupBody status={status.quiz} error={errors.quiz} onRetry={loadQuiz} hasData={!!quizStats} label="quiz results" cards={4}>
          <StatCard
            icon={QuizOutlinedIcon}
            label="Attempts"
            value={formatNumber(q.totalAttempts ?? 0)}
            note={`By ${formatNumber(q.students ?? 0)} ${Number(q.students) === 1 ? "student" : "students"}`}
            testId="kpi-quiz-attempts"
          />
          <StatCard
            icon={CheckCircleOutlineIcon}
            label="Passed"
            value={formatNumber(q.passed ?? 0)}
            note={quizMarked ? `Of ${formatNumber(quizMarked)} attempts with a pass mark` : "No attempts with a pass mark yet"}
            testId="kpi-quiz-passed"
          />
          <StatCard
            icon={HighlightOffOutlinedIcon}
            label="Below the pass mark"
            value={formatNumber(q.failed ?? 0)}
            note={Number(q.noPassMark) > 0 ? `${formatNumber(q.noPassMark)} more on lessons without a pass mark` : undefined}
            testId="kpi-quiz-failed"
          />
          <StatCard
            icon={GradingOutlinedIcon}
            label="Average score"
            value={Number(q.totalAttempts) > 0 && Number.isFinite(quizAvg) ? `${formatNumber(quizAvg, 1)}%` : DASH}
            note={Number(q.totalAttempts) > 0 ? "Every attempt" : "No attempts yet"}
            testId="kpi-quiz-average"
          />
          {quizStats && q.questionDataAvailable === false && (
            <Alert severity="info" sx={{ gridColumn: "1 / -1" }} data-testid="quiz-question-note">
              Results for each question (such as the most-missed questions) will appear once the new lesson player records
              every answer. Until then, scores are per quiz attempt.
            </Alert>
          )}
        </GroupBody>
      </FigureGroup>

      <FigureGroup
        id="support"
        title="Support and course survey"
        description={`Every support request must be answered within ${SLA_HOURS} hours.`}
        columns={4}
        action={
          <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap", rowGap: 1.5 }}>
            <Button component={RouterLink} to="/admin/support?view=open" variant="outlined">
              View support requests
            </Button>
            <Button component={RouterLink} to="/admin/feedback" variant="outlined">
              View survey responses
            </Button>
          </Stack>
        }
      >
        <GroupBody
          status={status.supportFeedback}
          error={errors.supportFeedback}
          onRetry={loadSupport}
          hasData={!!supportFeedbackStats}
          label="support and survey figures"
          cards={4}
        >
          <StatCard
            icon={SupportAgentOutlinedIcon}
            label="Open requests"
            value={formatNumber(tickets.open)}
            note={`${formatNumber(tickets.resolved)} resolved so far`}
          />
          <StatCard
            icon={ScheduleOutlinedIcon}
            label="Average time to resolve"
            testId="kpi-resolution"
            value={formatHours(tickets.avgResolutionHours)}
            tone={slow ? "warning" : "primary"}
            note={slow ? `Above the ${SLA_HOURS}-hour target` : `Target: within ${SLA_HOURS} hours`}
          />
          <StatCard
            icon={RateReviewOutlinedIcon}
            label="Survey responses"
            value={formatNumber(feedback.submissionsLast30Days)}
            note="In the last 30 days"
          />
          <StatCard
            icon={StarBorderIcon}
            label="Average rating"
            value={Number.isFinite(rating) && rating > 0 ? `${formatNumber(rating, 1)} out of 5` : DASH}
            note={Number.isFinite(rating) && rating > 0 ? "All survey responses" : "No ratings yet"}
          />
        </GroupBody>
      </FigureGroup>

      <Section
        id="trends"
        title="Monthly trends"
        description="New students, course completions and credited learning time per month (Ontario time)."
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{ alignItems: { xs: "stretch", md: "center" }, justifyContent: "space-between", mb: 2.5 }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              select
              id="trends-period"
              label="Period"
              value={period}
              onChange={(ev) => setPeriod(ev.target.value)}
              sx={{ minWidth: 200 }}
            >
              {PERIODS.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              id="trends-measure"
              label="Show"
              value={measure}
              onChange={(ev) => setMeasure(ev.target.value)}
              sx={{ minWidth: 200 }}
            >
              {Object.entries(MEASURES).map(([key, m]) => (
                <MenuItem key={key} value={key}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <ToggleButtonGroup value={view} exclusive onChange={(_, v) => v && setView(v)} aria-label="Show the trends as">
            <ToggleButton value="chart">
              <ShowChartOutlinedIcon aria-hidden sx={{ mr: 1 }} />
              Show as chart
            </ToggleButton>
            <ToggleButton value="table">
              <TableChartOutlinedIcon aria-hidden sx={{ mr: 1 }} />
              Show as table
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {status.trends === "failed" && !monthlyTrends ? (
          <ErrorState error={errors.trends} title="We couldn't load the trends" onRetry={loadTrends} />
        ) : !monthlyTrends ? (
          <LoadingState variant="inline" label="Loading trends…" />
        ) : (
          <Box aria-busy={status.trends === "loading"}>
            {status.trends === "loading" && <LoadingState variant="inline" label={`Loading ${periodLabel.toLowerCase()}…`} />}
            {status.trends === "failed" && <ErrorState compact error={errors.trends} onRetry={loadTrends} />}
            {chartData.length === 0 || !hasMeasure ? (
              <EmptyState
                compact
                title="Nothing to show for this period"
                description="No students registered, finished the course or had time credited in this period. Try a longer period."
              />
            ) : view === "chart" ? (
              <TrendsChart data={chartData} measure={measure} />
            ) : (
              <TrendsTable data={chartData} periodLabel={periodLabel} measure={measure} />
            )}
            <Typography sx={{ color: "text.secondary", mt: 1.5 }}>Showing {periodLabel.toLowerCase()}.</Typography>
          </Box>
        )}
      </Section>
    </Box>
  );
}
